/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ClipboardList, Copy, ExternalLink, Link2, Mail, Phone, RefreshCw, Search, Send, XCircle } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import CommerceItemImage from "../../../../components/store/CommerceItemImage.jsx";
import { SelectDropdown } from "../../../../components/ui/index.js";
import { editorPathForId } from "../../../../products/registry";

const COMMERCIAL_STATUSES = [
    ["solicitado", "Solicitado"],
    ["en_revision", "En revisión"],
    ["presupuesto_enviado", "Presupuesto enviado"],
    ["aceptado", "Aceptado"],
    ["rechazado", "Rechazado"],
    ["incidencia", "Incidencia"],
];

const PRODUCTION_STATUSES = [
    ["no_iniciado", "No iniciado"],
    ["preparacion", "Preparación"],
    ["en_produccion", "En producción"],
    ["entregado", "Entregado"],
    ["bloqueado", "Bloqueado"],
];

const PURCHASE_STATUSES = [
    ["no_disponible", "No disponible"],
    ["lista_para_compra", "Lista para compra"],
    ["en_carrito", "En carrito"],
    ["comprada", "Comprada"],
    ["caducada", "Caducada"],
    ["cancelada", "Cancelada"],
];

const COMMERCIAL_BADGE = {
    solicitado:          "badge-warning",
    en_revision:         "badge-info",
    presupuesto_enviado: "badge-secondary",
    aceptado:            "badge-success",
    rechazado:           "badge-error",
    incidencia:          "badge-error",
};

const PRODUCTION_BADGE = {
    no_iniciado:   "badge-ghost",
    preparacion:   "badge-info",
    en_produccion: "badge-info",
    entregado:     "badge-success",
    bloqueado:     "badge-error",
};

const PURCHASE_BADGE = {
    no_disponible:       "badge-ghost",
    lista_para_compra:   "badge-success",
    en_carrito:          "badge-info",
    comprada:            "badge-success",
    caducada:            "badge-warning",
    cancelada:           "badge-error",
};

const NFC_FUNCTIONS = [
    ["menu", "Carta o menú"], ["orders", "Pedidos a domicilio"],
    ["reservations", "Reservas"], ["reviews", "Reseñas"],
    ["contact", "Contacto o WhatsApp"], ["location", "Ubicación"],
    ["social", "Redes sociales"], ["all_in_one", "Todo en uno"],
    ["other_url", "Otra URL"],
];

function statusLabel(list, value) {
    return list.find(([v]) => v === value)?.[1] ?? value;
}

function fmtCurrency(value, currency = "EUR") {
    if (value === null || value === undefined || value === "") return "Sin precio";
    return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: currency || "EUR",
    }).format(Number(value));
}

function buildPurchaseUrl(path) {
    if (!path) return "";
    return `${window.location.origin}/${String(path).replace(/^\/+/, "")}`;
}

function formatApiError(err, fallback) {
    const data = err.response?.data;
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (data.detail) return data.detail;
    return Object.entries(data)
        .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(", ") : value}`)
        .join(" | ");
}

function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    }, [text]);
    return (
        <button className="btn btn-xs btn-ghost" title="Copiar" onClick={handleCopy}>
            {copied ? <span className="text-success text-xs">✓</span> : <Copy className="w-3 h-3" />}
        </button>
    );
}

export default function AdminCustomOrderRequests() {
    const [searchParams] = useSearchParams();
    const userId = searchParams.get("user_id") || "";
    const [requests, setRequests] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [search, setSearch] = useState("");
    const [commercialFilter, setCommercialFilter] = useState("");
    const [productionFilter, setProductionFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get("/custom-order-requests-admin/", {
                params: {
                    search: search || undefined,
                    commercial_status: commercialFilter || undefined,
                    production_status: productionFilter || undefined,
                    user_id: userId || undefined,
                },
            });
            const results = data.results ?? data;
            setRequests(results);
            setTotalCount(data.count ?? results.length);
        } catch (err) {
            console.error("Error fetching custom order requests:", err);
            setError(formatApiError(err, "No se pudieron cargar las solicitudes."));
        } finally {
            setLoading(false);
        }
    }, [search, commercialFilter, productionFilter, userId]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const updateRequest = async (requestId, payload) => {
        const previous = requests;
        setRequests((current) =>
            current.map((r) => r.id === requestId ? { ...r, ...payload } : r)
        );
        try {
            const { data } = await axios.patch(`/custom-order-requests-admin/${requestId}/`, payload);
            setRequests((current) =>
                current.map((r) => r.id === requestId ? data : r)
            );
        } catch (err) {
            console.error("Error updating custom order request:", err);
            setRequests(previous);
            setError(formatApiError(err, "No se pudo guardar el cambio en la solicitud."));
        }
    };

    const generatePurchaseLink = async (requestId) => {
        try {
            const { data } = await axios.post(`/custom-order-requests-admin/${requestId}/purchase-token/`, {
                expires_in_days: 30,
            });
            setRequests((current) =>
                current.map((r) => r.id === requestId ? data.request : r)
            );
            const url = buildPurchaseUrl(data.purchase_token?.purchase_url);
            if (url) {
                await navigator.clipboard.writeText(url);
            }
        } catch (err) {
            console.error("Error generating purchase link:", err);
            setError(formatApiError(err, "No se pudo generar el enlace privado."));
        }
    };

    const revokePurchaseLink = async (requestId) => {
        try {
            const { data } = await axios.post(`/custom-order-requests-admin/${requestId}/purchase-token/revoke/`);
            setRequests((current) =>
                current.map((r) => r.id === requestId ? data : r)
            );
        } catch (err) {
            console.error("Error revoking purchase link:", err);
            setError(formatApiError(err, "No se pudo revocar el enlace privado."));
        }
    };

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">
            {/* Toolbar */}
            <div className="glassCard rounded-xl p-3 z-40 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <ClipboardList className="w-6 h-6 text-primary" />
                        <div>
                            <h2 className="text-xl font-semibold">Solicitudes</h2>
                            <p className="text-sm text-base-content/60">
                                {totalCount} solicitudes comerciales
                            </p>
                        </div>
                    </div>
                    <button className="btn btn-sm btn-outline gap-2" onClick={fetchRequests}>
                        <RefreshCw className="w-4 h-4" />
                        Actualizar
                    </button>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                    <label className="input input-bordered input-sm flex items-center gap-2">
                        <Search className="w-4 h-4 opacity-60" />
                        <input
                            className="grow"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && fetchRequests()}
                            placeholder="Nombre, email, empresa, modelo"
                        />
                    </label>

                    <SelectDropdown
                        size="sm"
                        placeholder="Estado comercial"
                        value={commercialFilter}
                        onChange={setCommercialFilter}
                        options={[
                            { value: "", label: "Todos" },
                            ...COMMERCIAL_STATUSES.map(([value, label]) => ({ value, label })),
                        ]}
                    />

                    <SelectDropdown
                        size="sm"
                        placeholder="Estado producción"
                        value={productionFilter}
                        onChange={setProductionFilter}
                        options={[
                            { value: "", label: "Todos" },
                            ...PRODUCTION_STATUSES.map(([value, label]) => ({ value, label })),
                        ]}
                    />
                </div>
            </div>

            {/* Tabla */}
            <div className="glassCard rounded-xl flex-1 overflow-auto scrollbar_mod">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <span className="loading loading-spinner loading-lg text-primary" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full">
                        <div role="alert" className="alert alert-error max-w-lg shadow-lg">
                            <AlertTriangle className="w-5 h-5" />
                            <div>
                                <h3 className="font-semibold">Hay un problema con solicitudes</h3>
                                <div className="text-sm">{error}</div>
                            </div>
                            <button className="btn btn-sm btn-ghost" onClick={fetchRequests}>Reintentar</button>
                        </div>
                    </div>
                ) : requests.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full gap-2 text-center text-base-content/60">
                        <ClipboardList className="w-10 h-10 opacity-30" />
                        <p>No hay solicitudes con estos filtros.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table table-zebra">
                            <thead>
                                <tr>
                                    <th>Diseño</th>
                                    <th>Contacto</th>
                                    <th>Pedido</th>
                                    <th>Propuesta</th>
                                    <th>Comercial</th>
                                    <th>Producción</th>
                                    <th>Notas internas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map((request) => (
                                    <tr key={request.id}>
                                        {/* Diseño */}
                                        <td className="min-w-48">
                                            <div className="flex items-center gap-3">
                                                <CommerceItemImage
                                                    customKeyring={request.customized_keyring_detail}
                                                    alt={request.customized_keyring_detail?.name || `Modelo #${request.customized_keyring}`}
                                                    className="w-14 h-14"
                                                />
                                                <div className="min-w-0">
                                                    <div className="font-medium truncate">
                                                        {request.customized_keyring_detail?.name || `Modelo #${request.customized_keyring}`}
                                                    </div>
                                                    <div className="text-xs text-base-content/50">
                                                        #{request.id} · {new Date(request.created_at).toLocaleDateString("es-ES")}
                                                    </div>
                                                    <Link
                                                        className="btn btn-xs btn-ghost mt-1 gap-1"
                                                        to={editorPathForId(request.customized_keyring)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <ExternalLink className="w-3 h-3" />
                                                        Abrir diseño
                                                    </Link>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Contacto */}
                                        <td className="min-w-56">
                                            <div className="font-medium">{request.contact_name}</div>
                                            <div className="flex items-center gap-1 text-xs">
                                                <span>{request.contact_email}</span>
                                                <CopyButton text={request.contact_email} />
                                                <a
                                                    href={`mailto:${request.contact_email}?subject=Solicitud%20%23${request.id}`}
                                                    className="btn btn-xs btn-ghost"
                                                    title="Enviar email"
                                                >
                                                    <Mail className="w-3 h-3" />
                                                </a>
                                            </div>
                                            {request.contact_phone && (
                                                <a
                                                    href={`tel:${request.contact_phone}`}
                                                    className="flex items-center gap-1 text-xs text-base-content/60 hover:text-primary"
                                                    title="Llamar"
                                                >
                                                    <Phone className="w-3 h-3" />
                                                    {request.contact_phone}
                                                </a>
                                            )}
                                            {request.company_name && (
                                                <div className="text-xs text-base-content/50">{request.company_name}</div>
                                            )}
                                            {request.user_email && request.user_email !== request.contact_email && (
                                                <div className="text-xs text-base-content/40 mt-1" title="Cuenta registrada">
                                                    Cuenta: {request.user_email}
                                                </div>
                                            )}
                                        </td>

                                        {/* Pedido */}
                                        <td className="min-w-44">
                                            <div>{request.quantity} uds.</div>
                                            <div className="text-xs text-base-content/60">
                                                {request.material} · {request.color}
                                            </div>
                                            {request.finish && (
                                                <div className="text-xs text-base-content/60">Acabado: {request.finish}</div>
                                            )}
                                            {request.desired_date && (
                                                <div className="text-xs text-base-content/60">
                                                    Entrega: {new Date(request.desired_date).toLocaleDateString("es-ES")}
                                                </div>
                                            )}
                                            {request.notes && (
                                                <p className="text-xs mt-1 max-w-60 whitespace-pre-wrap text-base-content/70">
                                                    {request.notes}
                                                </p>
                                            )}
                                            {(request.nfc_config || /id=["'][^"']*insert_\d+_nfc_/i.test(request.customized_keyring_detail?.svg_2d || "")) && (
                                                <div className="mt-2 rounded-md bg-primary/10 p-2 text-xs">
                                                    <label className="font-semibold" htmlFor={`nfc-function-${request.id}`}>NFC</label>
                                                    <select
                                                        id={`nfc-function-${request.id}`}
                                                        className="select select-bordered select-xs mt-1 w-full"
                                                        value={request.nfc_config?.function || ""}
                                                        onChange={(event) => {
                                                            if (!event.target.value) {
                                                                setRequests((current) => current.map((item) => item.id === request.id ? { ...item, nfc_config: null } : item));
                                                                if (request.nfc_config) updateRequest(request.id, { nfc_config: null });
                                                                return;
                                                            }
                                                            const nfc_config = { ...(request.nfc_config || {}), function: event.target.value, value: request.nfc_config?.value || "" };
                                                            setRequests((current) => current.map((item) => item.id === request.id ? { ...item, nfc_config } : item));
                                                            if (request.nfc_config?.value) updateRequest(request.id, { nfc_config });
                                                        }}
                                                    >
                                                        <option value="">Elige una función</option>
                                                        {NFC_FUNCTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                                    </select>
                                                    <input
                                                        className="input input-bordered input-xs mt-1 w-full"
                                                        value={request.nfc_config?.value || ""}
                                                        onChange={(event) => setRequests((current) => current.map((item) => item.id === request.id ? { ...item, nfc_config: { ...(item.nfc_config || {}), value: event.target.value } } : item))}
                                                        onBlur={(event) => {
                                                            const nfc_config = { ...(request.nfc_config || {}), value: event.target.value };
                                                            if (nfc_config.function && nfc_config.value) updateRequest(request.id, { nfc_config });
                                                        }}
                                                        placeholder="URL o dato a programar"
                                                    />
                                                </div>
                                            )}
                                            <div className="mt-2 border-t border-base-300/60 pt-2">
                                                <label className="text-xs text-base-content/60 block mb-1">
                                                    Peso por unidad (g), necesario para envío
                                                </label>
                                                <input
                                                    className="input input-bordered input-xs w-full"
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    value={request.orca_estimated_grams ?? ""}
                                                    onChange={(e) =>
                                                        setRequests((current) =>
                                                            current.map((item) =>
                                                                item.id === request.id
                                                                    ? { ...item, orca_estimated_grams: e.target.value }
                                                                    : item
                                                            )
                                                        )
                                                    }
                                                    onBlur={(e) => updateRequest(request.id, {
                                                        orca_estimated_grams: e.target.value === "" ? null : Number(e.target.value),
                                                    })}
                                                    placeholder="Ej. 35"
                                                />
                                                <input
                                                    className="input input-bordered input-xs w-full mt-1"
                                                    value={request.orca_source || ""}
                                                    onChange={(e) =>
                                                        setRequests((current) =>
                                                            current.map((item) =>
                                                                item.id === request.id ? { ...item, orca_source: e.target.value } : item
                                                            )
                                                        )
                                                    }
                                                    onBlur={(e) => updateRequest(request.id, { orca_source: e.target.value })}
                                                    placeholder="Origen: OrcaSlicer 2.x"
                                                />
                                                {request.orca_estimated_at && (
                                                    <div className="text-[10px] text-base-content/50 mt-1">
                                                        Registrado {new Date(request.orca_estimated_at).toLocaleDateString("es-ES")}
                                                    </div>
                                                )}
                                            </div>
                                        </td>

                                        {/* Propuesta */}
                                        <td className="min-w-80">
                                            <div className="flex items-center justify-between gap-2 mb-2">
                                                <span className={`badge badge-sm ${PURCHASE_BADGE[request.purchase_status] ?? "badge-ghost"}`}>
                                                    {statusLabel(PURCHASE_STATUSES, request.purchase_status)}
                                                </span>
                                                <span className="text-xs font-semibold">
                                                    {fmtCurrency(request.approved_final_price, request.approved_currency)}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-[1fr_auto] gap-2 mb-2">
                                                <input
                                                    className="input input-bordered input-xs"
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={request.approved_final_price ?? ""}
                                                    onChange={(e) =>
                                                        setRequests((current) =>
                                                            current.map((item) =>
                                                                item.id === request.id
                                                                    ? { ...item, approved_final_price: e.target.value }
                                                                    : item
                                                            )
                                                        )
                                                    }
                                                    onBlur={(e) =>
                                                        updateRequest(request.id, {
                                                            approved_final_price: e.target.value === "" ? null : e.target.value,
                                                        })
                                                    }
                                                    placeholder="Precio final"
                                                />
                                                <input
                                                    className="input input-bordered input-xs w-16 uppercase"
                                                    value={request.approved_currency || "EUR"}
                                                    onChange={(e) =>
                                                        setRequests((current) =>
                                                            current.map((item) =>
                                                                item.id === request.id
                                                                    ? { ...item, approved_currency: e.target.value.toUpperCase() }
                                                                    : item
                                                            )
                                                        )
                                                    }
                                                    onBlur={(e) => updateRequest(request.id, { approved_currency: e.target.value.toUpperCase() || "EUR" })}
                                                    maxLength={3}
                                                />
                                            </div>

                                            <div className="grid grid-cols-2 gap-2 mb-2">
                                                <input
                                                    className="input input-bordered input-xs"
                                                    type="date"
                                                    value={request.proposal_valid_until || ""}
                                                    onChange={(e) =>
                                                        setRequests((current) =>
                                                            current.map((item) =>
                                                                item.id === request.id
                                                                    ? { ...item, proposal_valid_until: e.target.value }
                                                                    : item
                                                            )
                                                        )
                                                    }
                                                    onBlur={(e) => updateRequest(request.id, { proposal_valid_until: e.target.value || null })}
                                                />
                                                <select
                                                    className="select select-bordered select-xs"
                                                    value={request.purchase_status || "no_disponible"}
                                                    onChange={(e) => updateRequest(request.id, { purchase_status: e.target.value })}
                                                >
                                                    {PURCHASE_STATUSES.map(([value, label]) => (
                                                        <option key={value} value={value}>{label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <textarea
                                                className="textarea textarea-bordered textarea-xs w-full min-h-16"
                                                value={request.proposal_message || ""}
                                                onChange={(e) =>
                                                    setRequests((current) =>
                                                        current.map((item) =>
                                                            item.id === request.id
                                                                ? { ...item, proposal_message: e.target.value }
                                                                : item
                                                        )
                                                    )
                                                }
                                                onBlur={(e) => updateRequest(request.id, { proposal_message: e.target.value })}
                                                placeholder="Mensaje visible para el cliente"
                                            />

                                            <div className="flex flex-wrap gap-1 mt-2">
                                                <button
                                                    className="btn btn-xs btn-primary gap-1"
                                                    onClick={() => generatePurchaseLink(request.id)}
                                                    disabled={!request.approved_final_price || Number(request.approved_final_price) <= 0}
                                                    title="Generar y copiar enlace privado"
                                                >
                                                    <Link2 className="w-3 h-3" />
                                                    Generar
                                                </button>

                                                {request.active_purchase_url && (
                                                    <>
                                                        <CopyButton text={buildPurchaseUrl(request.active_purchase_url)} />
                                                        <a
                                                            className="btn btn-xs btn-ghost"
                                                            href={buildPurchaseUrl(request.active_purchase_url)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="Abrir enlace"
                                                        >
                                                            <ExternalLink className="w-3 h-3" />
                                                        </a>
                                                        <a
                                                            className="btn btn-xs btn-ghost"
                                                            href={`mailto:${request.contact_email}?subject=${encodeURIComponent(`Propuesta Customiz3D #${request.id}`)}&body=${encodeURIComponent(`Hola ${request.contact_name},\n\nYa tienes disponible tu propuesta para comprar este diseño:\n${buildPurchaseUrl(request.active_purchase_url)}\n\nUn saludo.`)}`}
                                                            title="Enviar por email"
                                                        >
                                                            <Send className="w-3 h-3" />
                                                        </a>
                                                        {request.contact_phone && (
                                                            <a
                                                                className="btn btn-xs btn-ghost"
                                                                href={`https://wa.me/${request.contact_phone.replace(/[^\d]/g, "")}?text=${encodeURIComponent(`Hola ${request.contact_name}, ya tienes disponible tu propuesta Customiz3D: ${buildPurchaseUrl(request.active_purchase_url)}`)}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title="Enviar por WhatsApp"
                                                            >
                                                                <Phone className="w-3 h-3" />
                                                            </a>
                                                        )}
                                                        <button
                                                            className="btn btn-xs btn-ghost text-error"
                                                            onClick={() => revokePurchaseLink(request.id)}
                                                            title="Revocar enlace"
                                                        >
                                                            <XCircle className="w-3 h-3" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>

                                        {/* Estado comercial */}
                                        <td>
                                            <div className="flex flex-col gap-1">
                                                <span className={`badge badge-sm ${COMMERCIAL_BADGE[request.commercial_status] ?? "badge-ghost"}`}>
                                                    {statusLabel(COMMERCIAL_STATUSES, request.commercial_status)}
                                                </span>
                                                <select
                                                    className="select select-bordered select-xs"
                                                    value={request.commercial_status}
                                                    onChange={(e) => updateRequest(request.id, { commercial_status: e.target.value })}
                                                >
                                                    {COMMERCIAL_STATUSES.map(([value, label]) => (
                                                        <option key={value} value={value}>{label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </td>

                                        {/* Estado producción */}
                                        <td>
                                            <div className="flex flex-col gap-1">
                                                <span className={`badge badge-sm ${PRODUCTION_BADGE[request.production_status] ?? "badge-ghost"}`}>
                                                    {statusLabel(PRODUCTION_STATUSES, request.production_status)}
                                                </span>
                                                <select
                                                    className="select select-bordered select-xs"
                                                    value={request.production_status}
                                                    onChange={(e) => updateRequest(request.id, { production_status: e.target.value })}
                                                >
                                                    {PRODUCTION_STATUSES.map(([value, label]) => (
                                                        <option key={value} value={value}>{label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </td>

                                        {/* Notas internas */}
                                        <td className="min-w-64">
                                            <textarea
                                                className="textarea textarea-bordered textarea-sm w-full min-h-20"
                                                value={request.internal_notes || ""}
                                                onChange={(e) =>
                                                    setRequests((current) =>
                                                        current.map((item) =>
                                                            item.id === request.id
                                                                ? { ...item, internal_notes: e.target.value }
                                                                : item
                                                        )
                                                    )
                                                }
                                                onBlur={(e) => updateRequest(request.id, { internal_notes: e.target.value })}
                                                placeholder="Notas solo para admin"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
